import store from '@/store';
import ErrorMessage from './ErrorMessage';
import 'cross-fetch/polyfill';

const defaultOptions = {
  headers: {
    'Cache-Control': 'no-cache'
  }
};

const defaultPostOptions = {
  ...defaultOptions,
  method: 'POST',
  headers: {
    ...defaultOptions.headers,
    'Content-Type': 'application/json',
  },
  cache: 'no-store',
};

const defaultGetOptions = {
  ...defaultOptions,
  method: 'GET',
  headers: {
    ...defaultOptions.headers
  }
};

export default class Requests {
  /**
   * Performs a GET request and returns JSON response
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} - JSON response
   */
  static async get(url, options = defaultGetOptions) {
    try {
      const res = await fetch(url, options);

      if (!res.ok) {
        throw new Error(`Bad response from server: ${res.status}`);
      }

      const response = await res.json();
      console.log("==================================GET", response);
      return response;
    } catch (err) {
      console.error('GET request failed:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Performs a GET request and returns XML response
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @returns {Promise<Document>} - XML document
   */
  static async getXml(url, options = defaultGetOptions) {
    try {
      const res = await fetch(url, options);

      if (!res.ok) {
        throw new Error(`Bad response from server: ${res.status}`);
      }

      const text = await res.text();
      return new window.DOMParser().parseFromString(text, "text/xml");
    } catch (err) {
      console.error('XML request failed:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Performs a POST request
   * @param {string} url - The URL to fetch
   * @param {Object} data - The data to send
   * @param {Object} optionParam - Custom fetch options
   * @returns {Promise<Object>} - JSON response
   */
  static async post(url = '', data = {}, optionParam) {
    try {
      const options = optionParam || { ...defaultPostOptions };

      if (!optionParam) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`Bad response from server: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('POST request failed:', url, err);
      return Promise.reject(err);
    }
  }

  /**
   * Fetches custom design for a building
   * @param {number} bid - Building ID
   * @returns {Promise<Object>} - Custom design data
   */
  static async fetchCustomDesign(bid = 2) {
    const response = await this.get(
      `${process.env.API_PORTAL}/bid/${bid}/customizer`
    );
    return response[0];
  }

  /**
   * Fetches building ID, tries POST if GET fails
   * @returns {Promise<Object>} - Building ID data
   */
  static async fetchBuildingId() {
    try {
      const response = await this.get(`${process.env.API_PORTAL}/getbid/`);
      console.log('RESPONSE GET', response);

      if (response && response.length > 0) {
        return response[0];
      }

      // If GET returns empty array, try POST
      const responsePost = await this.post(`${process.env.API_PORTAL}/getbid/`);
      console.log('RESPONSE POST', responsePost);
      return responsePost;
    } catch (err) {
      console.error('Error fetching building ID:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Fetches building details
   * @param {number|string} bid - Building ID
   * @returns {Promise<Object>} - Building details
   */
  static async fetchBuildingDetails(bid) {
    try {
      return await this.get(
        `${process.env.API_FIAS}/getBuildingDetails/${bid}`
      );
    } catch (err) {
      console.error('Could not fetch data for building details:', bid, err);
      return {};
    }
  }

  /**
   * Sucht ein Geraet anhand seines Fingerprints.
   * Gibt null zurueck wenn nicht gefunden oder API kein Fingerprint-Lookup unterstuetzt.
   * @param {string} fingerprint - SHA-256 Fingerprint des Geraets
   * @returns {Promise<Object|null>} - Geraetedaten oder null
   */
  static async fetchDeviceByFingerprint(fingerprint, device) {
    console.log('=======================================[Fingerprint] lookup:', fingerprint);
    //https://stagingapi.digmedia.net/api/v1/getbid/devices/?fingerprint=6327d6c991625dc26327d6c991625dc2000000000000002a6327d6c991625dc2&serialNumber=211KKYUKJ362&mac=7c:64:6c:04:58:d7
    try {
      const result = await this.get(
        `${process.env.API_PORTAL}/getbid/devices/?fingerprint=${encodeURIComponent(fingerprint)}&serialNumber=${encodeURIComponent(device.serialNumber)}&mac=${encodeURIComponent(device.mac)}`
      );
      // API kann Array oder einzelnes Objekt zurueckgeben
      if (Array.isArray(result)) return result.length > 0 ? result[0] : null;
      return result && result.id ? result : null;
    } catch (err) {
      // Endpoint nicht vorhanden oder kein Ergebnis → null zurueck (kein Abbruch)
      console.log('[Fingerprint] lookup not available or no result:', err.message);
      return null;
    }
  }

  /**
   * Creates a new device
   * @param {Object} device - Device data
   * @returns {Promise<Object>} - Created device data
   */
  static async postCreateDevice(device) {
    try {
      const response = await this.post(
        `${process.env.API_PORTAL}/getbid/devices/`,
        device
      );

      store.dispatch('notification/new', {
        title: 'Saved',
        message: `DeviceId: ${JSON.stringify(response)}`,
      });

      console.log(response);
      return response;
    } catch (err) {
      console.error('Failed post device:', device, err);
      store.dispatch('notification/new', {
        title: 'Failed post device',
        message: `DeviceId: ${JSON.stringify(device)}, Err: ${err}`,
      });
      return Promise.reject(err);
    }
  }

  /**
   * Updates device data (IP, MAC, resWidth, resHeight, Fingerprint, Version, Firmware etc.)
   * DeviceId, buildingId and room are NOT updated.
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @param {string} params.deviceId - Device ID
   * @param {Object} params.data - Data fields to update
   * @returns {Promise<Object>} - Updated device data
   */
  static async updateDeviceInfo({ bid, deviceId, data }) {
    if (bid === undefined || deviceId === undefined) {
      return ErrorMessage.missingParameter({ bid, deviceId });
    }

    try {
      const response = await fetch(
        `${process.env.API_PORTAL}/bid/${bid}/devices/${deviceId}`,
        {
          ...defaultPostOptions,
          //method: 'PUT',
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`Bad response from server: ${response.status}`);
      }

      const result = await response.json();
      store.dispatch('notification/new', {
        title: 'Device aktualisiert',
        message: `IP: ${data.ip}, MAC: ${data.mac}`,
      });
      return result;
    } catch (err) {
      console.error('Failed to update device:', err);
      store.dispatch('notification/new', {
        title: 'Update fehlgeschlagen',
        message: String(err),
      });
      return Promise.reject(err);
    }
  }

  /**
   * Fetches device information
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @param {string} params.deviceId - Device ID
   * @returns {Promise<Object>} - Device information
   */
  static async fetchDeviceInfo({ bid, deviceId }) {
    if (bid === undefined || deviceId === undefined) {
      return ErrorMessage.missingParameter({ bid, deviceId });
    }

    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/devices/${deviceId}`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches VOD categories
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @returns {Promise<Object>} - VOD categories
   */
  static async fetchVodCategory({ bid }) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/vod-genres/`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches VOD content
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @returns {Promise<Object>} - VOD content
   */
  static async fetchVod({ bid }) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/vod/`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches channel list
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @returns {Promise<Object>} - Channel list
   */
  static async fetchChannelList({ bid }) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/channels/`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  static async fetchEpgByChannels({channels}) {
    if (channels === undefined) {
      return ErrorMessage.missingParameter({ channels });
    }

    try {
      const pool = [];
      const from = Math.round(+new Date() / 1000);

      for(let channel of channels) {
        const response = await this.getXml(
          `${process.env.EPG_API}/epg?channel_id=${channel.epgChannelId}&number=110&from=${from}`
        );
        pool.push(response);
      }
      return pool;
    } catch (err) {
      console.error('Failed to fetch EPG by channels:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Fetches TV packages for a device
   * @param {Object} params - Parameters
   * @param {string} params.deviceId - Device ID
   * @returns {Promise<Object>} - TV packages
   */
  static async fetchTvPackages({deviceId}) {
    if (deviceId === undefined) {
      return ErrorMessage.missingParameter({ deviceId });
    }

    try {
      return await this.get(
        `${process.env.TV_PACKAGES}/packages/devices/${deviceId}`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches submenu for a widget
   * @param {number|string} id - Widget ID
   * @returns {Promise<Object>} - Submenu data
   */
  static async fetchSubMenu(id) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/widgetId/${id}/submenu`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches VOD submenu
   * @param {number|string} id - Widget ID
   * @param {number|string} bid - Building ID
   * @returns {Promise<Object>} - VOD submenu data
   */
  static async fetchVodSubMenu(id, bid) {
    try {
      const response = await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/vod/`
      );
      console.log("===================================fetchVodSubMenu", id, response);
      return response;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches all EPG data
   * @returns {Promise<Object>} - EPG data
   */
  static async fetchAllEPGData() {
    try {
      return await this.get(`${process.env.EPG_API}/channels`);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches EPG data for a specific channel
   * @param {number|string} id - Channel ID
   * @returns {Promise<Object>} - EPG data for channel
   */
  static async fetchEPGDataById(id) {
    try {
      return await this.get(`${process.env.EPG_API}/channel/${id}`);
    } catch (err) {
      return Promise.reject(err);
    }
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Request.js